/** Dotted past/future focus control shared by the four planning views. */
import { HORIZON_PRESETS, normalizeHorizon, isHorizonActive } from '../../../engine/core/horizon.js';
import { t } from '../../../shared/i18n/index.js';

const ALL = HORIZON_PRESETS.length + 1;
// Texts are read when rendering (the language loads after this module).
const SIDES = {
  past: {
    get label() { return t('views.horizon.past.label'); },
    get help() { return t('views.horizon.past.help'); },
  },
  ahead: {
    get label() { return t('views.horizon.ahead.label'); },
    get help() { return t('views.horizon.ahead.help'); },
  },
};
const combinedHelp = () => t('views.horizon.combinedHelp');
// Keep disclosure state through view rerenders, but start each opened map folded.
let disclosureDocId;
let expanded = false;
let nextControlId = 0;

function stepOf(value) {
  if (!value) return ALL;
  const index = HORIZON_PRESETS.findIndex((p) => p.key === value);
  if (index >= 0) return index + 1;
  // Existing exact-date preferences remain intact until the handle is moved.
  const days = Math.abs((new Date(`${value}T00:00:00`) - new Date()) / 86400000);
  const nearest = HORIZON_PRESETS.reduce((best, p, i, list) =>
    Math.abs(p.days - days) < Math.abs(list[best].days - days) ? i : best, 0);
  return nearest + 1;
}

function labelOf(side, value) {
  if (!value) return t('views.horizon.anyTime');
  const preset = HORIZON_PRESETS.find((p) => p.key === value);
  if (preset) return t(`views.horizon.${side}.${preset.unit}`, { count: preset.count });
  return t(side === 'past' ? 'views.horizon.since' : 'views.horizon.through', { date: value });
}

function valueHtml(side) {
  return `<output data-value="${side}" title="${SIDES[side].help} ${combinedHelp()}">
    <span class="horizon-label">${SIDES[side].label}</span>
    <span class="horizon-period"></span>
  </output>`;
}

function halfHtml(side) {
  const steps = [...HORIZON_PRESETS, { label: t('views.horizon.allTime') }];
  return `<div class="horizon-half ${side}">
    <div class="horizon-dots" aria-hidden="true">${steps.map((step, i) => {
      const position = i / (ALL - 1) * 100;
      return `<span class="horizon-dot${[0, 6, 10, 22, 23].includes(i) ? ' major' : ''}" data-step="${i + 1}"
        style="left:${side === 'past' ? 100 - position : position}%" title="${step.label}"></span>`;
    }).join('')}</div>
    <input type="range" class="horizon-range" data-side="${side}" min="1" max="${ALL}" step="1"
      ${side === 'past' ? 'dir="rtl"' : ''} aria-label="${SIDES[side].label}"
      aria-description="${SIDES[side].help} ${combinedHelp()}" />
  </div>`;
}

/** Called with the view's current state after rendering its breadcrumbs. */
export function renderHorizonControl(container, horizon, onChange, docId) {
  if (!container || !onChange) return;
  const crumbs = container.querySelector('.breadcrumb');
  const host = container.querySelector('[data-horizon-host]');
  if (!crumbs && !host) return;
  if (docId !== disclosureDocId) {
    disclosureDocId = docId;
    expanded = false;
  }
  let group = host || crumbs.closest('.view-focus-controls');
  if (!group) {
    group = document.createElement('div');
    group.className = 'view-focus-controls';
    crumbs.before(group);
    group.append(crumbs);
  }
  group.classList.add('view-focus-controls');
  let disclosure = group.querySelector('.horizon-disclosure');
  if (!disclosure) {
    disclosure = document.createElement('div');
    disclosure.className = 'horizon-disclosure';
    const controlId = `horizon-settings-${++nextControlId}`;
    disclosure.innerHTML = `<button type="button" class="horizon-toggle" aria-expanded="false" aria-controls="${controlId}">
      <span class="horizon-toggle-label">${t('views.horizon.title')}</span><span class="horizon-toggle-arrow" aria-hidden="true">&gt;</span>
    </button>`;
    const control = document.createElement('div');
    control.className = 'horizon-control';
    control.id = controlId;
    control.hidden = true;
    control.setAttribute('role', 'group');
    control.setAttribute('aria-label', t('views.horizon.title'));
    control.innerHTML = `
      <div class="horizon-timeline">
        ${valueHtml('past')}
        <div class="horizon-scale">${halfHtml('past')}<span class="horizon-now">${t('views.horizon.now')}</span>${halfHtml('ahead')}</div>
        ${valueHtml('ahead')}
      </div>
      <div class="horizon-options">
        <label title="${t('views.horizon.inListTitle')}"><input type="checkbox" data-filter="inExecutionOnly" />${t('views.horizon.inList')}</label>
        <button type="button" class="horizon-reset" title="${t('views.horizon.resetTitle')}">${t('views.horizon.reset')}</button>
      </div>`;
    disclosure.append(control);
    group.append(disclosure);
    // Keep gestures over the control out of canvas pan/zoom handlers.
    for (const event of ['pointerdown', 'mousedown', 'touchstart', 'wheel', 'keydown']) {
      disclosure.addEventListener(event, (e) => e.stopPropagation());
    }
    disclosure.querySelector('.horizon-toggle').addEventListener('click', (event) => {
      event.stopPropagation();
      expanded = !expanded;
      paintDisclosure(disclosure);
    });
    control.addEventListener('input', (event) => {
      const side = event.target.dataset.side;
      if (!side) return;
      const value = HORIZON_PRESETS[Number(event.target.value) - 1]?.key || null;
      paintSide(control, side, value);
    });
    control.addEventListener('change', (event) => {
      const { side, filter } = event.target.dataset;
      if (side) control.onHorizonChange({ [side]: HORIZON_PRESETS[Number(event.target.value) - 1]?.key || null });
      if (filter) control.onHorizonChange({ [filter]: event.target.checked });
    });
    control.querySelector('.horizon-reset').addEventListener('click', () =>
      control.onHorizonChange({ past: null, ahead: null, inExecutionOnly: false }));
  }
  const control = disclosure.querySelector('.horizon-control');
  control.onHorizonChange = onChange;
  const h = normalizeHorizon(horizon);
  for (const side of ['past', 'ahead']) {
    const input = control.querySelector(`[data-side="${side}"]`);
    if (document.activeElement !== input) {
      input.value = stepOf(h[side]);
      paintSide(control, side, h[side]);
    }
  }
  control.querySelector('[data-filter="inExecutionOnly"]').checked = h.inExecutionOnly;
  const active = isHorizonActive(h);
  control.classList.toggle('active', active);
  disclosure.classList.toggle('active', active);
  control.querySelector('.horizon-reset').disabled = !active;
  paintDisclosure(disclosure);
}

function paintDisclosure(disclosure) {
  const toggle = disclosure.querySelector('.horizon-toggle');
  disclosure.classList.toggle('expanded', expanded);
  disclosure.querySelector('.horizon-control').hidden = !expanded;
  toggle.setAttribute('aria-expanded', String(expanded));
  toggle.setAttribute('aria-label', expanded ? t('views.horizon.hide') : t('views.horizon.show'));
  const filterActive = disclosure.classList.contains('active');
  toggle.title = expanded
    ? t(filterActive ? 'views.horizon.hideActive' : 'views.horizon.hide')
    : t(filterActive ? 'views.horizon.showActive' : 'views.horizon.show');
  toggle.querySelector('.horizon-toggle-arrow').textContent = expanded ? '<' : '>';
}

function paintSide(control, side, value) {
  const label = labelOf(side, value);
  control.querySelector(`[data-value="${side}"] .horizon-period`).textContent = label;
  const input = control.querySelector(`[data-side="${side}"]`);
  input.setAttribute('aria-valuetext', label);
  input.title = `${t('views.horizon.sliderValue', { side: SIDES[side].label, period: label })} ${SIDES[side].help} ${combinedHelp()}`;
  control.querySelectorAll(`.horizon-half.${side} .horizon-dot`).forEach((dot) =>
    dot.classList.toggle('included', Number(dot.dataset.step) <= stepOf(value)));
}
